/**
 * Future Closure Panel Component
 * Main component for Future Forced Closure calculations
 * Displays on Report Detail page as a collapsible panel
 * 
 * P1: Includes Assumptions & Traceability Log
 * P2: Live preview and recalculate button
 * P3: Case-specific configuration support
 * P4: Enhanced data validation and warnings
 */

import React, { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { useAuth } from '../contexts/TestAuthContext';
import api from '../utils/api';
import { FinancialValue } from './shared/FinancialValue';
import {
  calculateFutureClosure,
  runSensitivityAnalysis,
  runMonteCarloSimulation,
  getFutureClosureCalculations,
  getCaseConfig,
  saveCaseConfig,
  setTokenGetter,
  FutureClosureCalculationParams,
  SensitivityParams,
  MonteCarloParams,
  CaseConfig,
  EdgeCaseConfig
} from '../services/futureClosureApi';
import FutureClosureForm from './FutureClosureForm';
import FutureClosureResults from './FutureClosureResults';
import SensitivityTornadoChart from './SensitivityTornadoChart';
import MonteCarloResults from './MonteCarloResults';
import FutureClosureAssumptionsLog from './FutureClosureAssumptionsLog';
import FutureClosureDataWarnings from './FutureClosureDataWarnings';
import FutureClosureEdgeCases from './FutureClosureEdgeCases';
import QuickExpenseEntryPanel from './QuickExpenseEntryPanel';
import ScenarioComparisonPanel from './ScenarioComparisonPanel';

interface FutureClosurePanelProps {
  reportId: string;
  reportData?: any;
}

export default function FutureClosurePanel({ reportId, reportData }: FutureClosurePanelProps) {
  const { getToken } = useAuth();
  
  // Debug: Log report ID and data
  useEffect(() => {
    console.log('FutureClosurePanel - Report ID:', reportId);
    console.log('FutureClosurePanel - Report Data:', reportData);
  }, [reportId, reportData]);
  
  // State
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [calculationResult, setCalculationResult] = useState<any>(null);
  const [sensitivityResult, setSensitivityResult] = useState<any>(null);
  const [monteCarloResult, setMonteCarloResult] = useState<any>(null);
  const [calculationHistory, setCalculationHistory] = useState<any[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [sensitivityLoading, setSensitivityLoading] = useState(false);
  const [monteCarloLoading, setMonteCarloLoading] = useState(false);
  
  // P3: Case-specific configuration state
  const [caseConfig, setCaseConfig] = useState<CaseConfig | null>(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [configSaveMessage, setConfigSaveMessage] = useState<string | null>(null);
  
  // P4: Warnings dismissed state
  const [warningsDismissed, setWarningsDismissed] = useState(false);
  
  // Edge case configuration state
  const [edgeCaseConfig, setEdgeCaseConfig] = useState<EdgeCaseConfig>({});
  
  // Quick expense entry state
  const [expenseData, setExpenseData] = useState<any>(null);
  const [expenseSources, setExpenseSources] = useState<any>(null);
  
  // Handle edge case config changes
  const handleEdgeCaseConfigChange = (config: EdgeCaseConfig) => {
    setEdgeCaseConfig(config);
  };
  
  // Handle expense data changes from QuickExpenseEntryPanel
  const handleExpensesChange = (expenses: any, sources: any) => {
    setExpenseData(expenses);
    setExpenseSources(sources);
  };
  
  // Save expenses to report and trigger recalculation
  const handleSaveExpenses = async (expenses: any, sources: any) => {
    try {
      const token = await getToken();
      await api.put(`/api/v1/reports/${reportId}/expenses`, {
        monthly_expenses: expenses,
        expense_sources: sources
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Update local expense data
      setExpenseData(expenses);
      setExpenseSources(sources);
      
      console.log('Expenses saved successfully:', expenses);
    } catch (err) {
      console.error('Error saving expenses:', err);
      throw err;
    }
  };

  // Initialize token getter
  useEffect(() => {
    setTokenGetter(getToken);
  }, [getToken]);

  // Load calculation history and case config when panel expands
  useEffect(() => {
    if (isExpanded && !calculationHistory.length) {
      loadCalculationHistory();
      loadCaseConfig();
    }
  }, [isExpanded]);
  
  // P3: Load case-specific configuration
  const loadCaseConfig = async () => {
    try {
      const data = await getCaseConfig(reportId);
      if (data.has_custom_config && data.config) {
        setCaseConfig(data.config);
      }
    } catch (err: any) {
      console.error('Error loading case config:', err);
    }
  };
  
  // P3: Save current parameters as case config
  const handleSaveConfig = async (parameters: FutureClosureCalculationParams) => {
    setSavingConfig(true);
    setConfigSaveMessage(null);
    try {
      const config: CaseConfig = {
        aggression: parameters.aggression,
        use_best_base: parameters.use_best_base,
        base_months: parameters.base_months,
        closure_duration_months: parameters.closure_duration_months,
        custom_continuations: parameters.custom_continuations,
        custom_one_time_costs: parameters.custom_one_time_costs || null,
      };
      await saveCaseConfig(reportId, config);
      setCaseConfig(config);
      setConfigSaveMessage('Configuration saved for this case');
      setTimeout(() => setConfigSaveMessage(null), 3000);
    } catch (err: any) {
      console.error('Error saving case config:', err);
      setConfigSaveMessage('Failed to save configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const loadCalculationHistory = async () => {
    try {
      const data = await getFutureClosureCalculations(reportId);
      setCalculationHistory(data.calculations || []);
    } catch (err: any) {
      console.error('Error loading calculation history:', err);
    }
  };

  const handleCalculate = async (parameters: FutureClosureCalculationParams) => {
    setLoading(true);
    setError(null);
    setCalculationResult(null);
    setMonteCarloResult(null);

    try {
      // Include edge case configuration in the calculation parameters
      const calculationParams = {
        ...parameters,
        edge_case_config: edgeCaseConfig
      };
      
      const result = await calculateFutureClosure(reportId, calculationParams);
      setCalculationResult(result);
      
      // Reload history
      await loadCalculationHistory();
      
      // Auto-scroll to results
      setTimeout(() => {
        document.getElementById('future-closure-results')?.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
      
    } catch (err: any) {
      console.error('Error calculating future closure:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to calculate future closure');
    } finally {
      setLoading(false);
    }
  };

  const handleRunSensitivity = async (parameters: SensitivityParams) => {
    setSensitivityLoading(true);
    setError(null);

    try {
      const result = await runSensitivityAnalysis(reportId, parameters);
      setSensitivityResult(result);
      
      // Auto-scroll to sensitivity results
      setTimeout(() => {
        document.getElementById('sensitivity-results')?.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
      
    } catch (err: any) {
      console.error('Error running sensitivity analysis:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to run sensitivity analysis');
    } finally {
      setSensitivityLoading(false);
    }
  };

  const handleRunMonteCarlo = async (parameters: MonteCarloParams) => {
    setMonteCarloLoading(true);
    setError(null);

    try {
      const result = await runMonteCarloSimulation(reportId, parameters);
      setMonteCarloResult(result);
      
      // Auto-scroll to Monte Carlo results
      setTimeout(() => {
        document.getElementById('monte-carlo-results')?.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
      
    } catch (err: any) {
      console.error('Error running Monte Carlo:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to run Monte Carlo simulation');
    } finally {
      setMonteCarloLoading(false);
    }
  };

  const loadHistoricalCalculation = async (calculationId: string) => {
    try {
      const calc = calculationHistory.find(c => c.id === calculationId);
      if (calc) {
        setCalculationResult({
          calculation_id: calc.id,
          report_id: calc.report_id,
          results: calc.results
        });
        setShowHistory(false);
        
        // Scroll to results
        setTimeout(() => {
          document.getElementById('future-closure-results')?.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }, 100);
      }
    } catch (err: any) {
      console.error('Error loading historical calculation:', err);
      setError('Failed to load calculation');
    }
  };

  return (
    <div className="rounded-md overflow-hidden border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      {/* Header */}
      <div
        className="p-4 cursor-pointer transition-all"
        style={{ backgroundColor: 'var(--color-bg-elevated)', borderBottom: '1px solid var(--color-border)' }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <svg
              className="w-6 h-6"
              style={{ color: 'var(--color-needs-human-text)' }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
            <div>
              <h3 className="cs-h3" style={{ color: 'var(--color-text-primary)' }}>
                Future Forced Closure Calculator
              </h3>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                3-Year Pro Forma Loss Projection (Toxic Remediation)
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {calculationHistory.length > 0 && (
              <span className="px-3 py-1 rounded-full text-sm font-medium" style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'var(--color-on-brand)' }}>
                {calculationHistory.length} calculation{calculationHistory.length !== 1 ? 's' : ''}
              </span>
            )}

            <svg
              className={`w-6 h-6 transition-transform duration-300 ${
                isExpanded ? 'transform rotate-180' : ''
              }`}
              style={{ color: 'var(--color-on-brand)' }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Expandable Content */}
      {isExpanded && (
        <div className="p-6 space-y-6">
          {/* P4: Data Warnings */}
          {!warningsDismissed && (
            <FutureClosureDataWarnings 
              reportData={reportData} 
              onDismiss={() => setWarningsDismissed(true)}
            />
          )}
          
          {/* Info Banner */}
          <div className="border rounded-md p-4" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
            <div className="flex items-start space-x-3">
              <svg
                className="w-5 h-5 mt-0.5 flex-shrink-0"
                style={{ color: 'var(--color-needs-human-text)' }}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
                <p className="font-medium mb-1">About Future Forced Closure Calculations</p>
                <p style={{ color: 'var(--color-text-secondary)' }}>
                  This module calculates anticipated business interruption losses for a <strong>forced 3-year closure</strong> 
                  due to toxic remediation (e.g., PFAS contamination). It generates pro forma projections with 
                  defensible assumptions for opening settlement demands.
                </p>
              </div>
            </div>
          </div>
          
          {/* Quick Expense Entry Panel - Maximize Claim Recovery */}
          <QuickExpenseEntryPanel
            reportId={reportId}
            reportData={reportData}
            closureDurationMonths={36}
            onExpensesChange={handleExpensesChange}
            onSave={handleSaveExpenses}
          />
          
          {/* P3: Case Config Notice */}
          {caseConfig && (
            <div className="border rounded-md p-3 flex items-center justify-between" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
              <div className="flex items-center space-x-2">
                <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  <strong>Custom configuration loaded</strong> for this case
                </span>
              </div>
              <button
                onClick={() => setCaseConfig(null)}
                className="text-xs underline"
                style={{ color: 'var(--color-needs-human-text)' }}
              >
                Reset to defaults
              </button>
            </div>
          )}
          
          {configSaveMessage && (
            <div
              className="p-3 rounded-md text-sm"
              style={
                configSaveMessage.includes('Failed')
                  ? { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-needs-human-text)' }
                  : { backgroundColor: 'transparent', color: 'var(--color-verified)' }
              }
            >
              {configSaveMessage}
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="border rounded-md p-4" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
              <div className="flex items-start space-x-3">
                <svg
                  className="w-5 h-5 mt-0.5 flex-shrink-0"
                  style={{ color: 'var(--color-needs-human-text)' }}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>Error</p>
                  <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>{error}</p>
                </div>
                <button
                  onClick={() => setError(null)}
                  style={{ color: 'var(--color-needs-human-text)' }}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* History Toggle */}
          {calculationHistory.length > 0 && (
            <div className="flex justify-end">
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="text-sm font-medium flex items-center space-x-1"
                style={{ color: 'var(--color-needs-human-text)' }}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>{showHistory ? 'Hide' : 'View'} Calculation History</span>
              </button>
            </div>
          )}

          {/* Calculation History */}
          {showHistory && calculationHistory.length > 0 && (
            <div className="border rounded-md p-4" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
              <h4 className="font-medium mb-3" style={{ color: 'var(--color-text-primary)' }}>Previous Calculations</h4>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {calculationHistory.map((calc) => (
                  <button
                    key={calc.id}
                    onClick={() => loadHistoricalCalculation(calc.id)}
                    className="w-full text-left border rounded-md p-3 transition-colors"
                    style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                          {new Date(calc.created_at).toLocaleString('en-US')}
                        </p>
                        <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                          Aggression: {calc.parameters?.aggression || 50}/100 • 
                          Demand: <FinancialValue value={calc.results?.total_demand} />
                        </p>
                      </div>
                      <svg className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Calculation Form */}
          <div id="future-closure-form">
            <FutureClosureForm
              onCalculate={handleCalculate}
              loading={loading}
              reportData={reportData}
              initialConfig={caseConfig}
            />
          </div>
          
          {/* Edge Case Overrides (Collapsible) */}
          <FutureClosureEdgeCases
            reportId={reportId}
            onConfigChange={handleEdgeCaseConfigChange}
            initialConfig={edgeCaseConfig}
            entityType={reportData?.business_type}
          />

          {/* Results Display */}
          {calculationResult && (
            <div id="future-closure-results">
              <FutureClosureResults
                result={calculationResult.results}
                reportId={reportId}
                calculationId={calculationResult.calculation_id}
                onRunMonteCarlo={handleRunMonteCarlo}
                onRunSensitivity={handleRunSensitivity}
                monteCarloLoading={monteCarloLoading}
                sensitivityLoading={sensitivityLoading}
                onRecalculate={() => {
                  // P3: Scroll back to form for recalculation
                  setCalculationResult(null);
                  setSensitivityResult(null);
                  setMonteCarloResult(null);
                  document.getElementById('future-closure-form')?.scrollIntoView({ 
                    behavior: 'smooth',
                    block: 'start'
                  });
                }}
              />
              
              {/* P3: Save Configuration Button */}
              <div className="mt-4 flex items-center justify-between p-4 rounded-md border" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>Save Configuration for This Case</p>
                  <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>Save current parameters to quickly recalculate with same settings</p>
                </div>
                <button
                  onClick={() => handleSaveConfig(calculationResult.results?.parameters || {})}
                  disabled={savingConfig}
                  className="px-4 py-2 rounded-md text-sm font-medium transition-all"
                  style={
                    savingConfig
                      ? { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-ghost)', cursor: 'not-allowed' }
                      : { backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }
                  }
                >
                  {savingConfig ? 'Saving...' : <span className="inline-flex items-center gap-1"><Save size={14} /> Save Config</span>}
                </button>
              </div>
              
              {/* P1: Assumptions & Traceability Log */}
              <div className="mt-6">
                <FutureClosureAssumptionsLog calculationResult={calculationResult.results} />
              </div>
            </div>
          )}

          {/* Sensitivity Analysis Results */}
          {sensitivityResult && (
            <div id="sensitivity-results">
              <SensitivityTornadoChart result={sensitivityResult.results} />
            </div>
          )}

          {/* Monte Carlo Results */}
          {monteCarloResult && (
            <div id="monte-carlo-results">
              <MonteCarloResults result={monteCarloResult.results} />
            </div>
          )}

          {/* Scenario Comparison Panel */}
          <div className="mt-6 border rounded-md p-6" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
            <ScenarioComparisonPanel
              reportId={reportId}
              currentCalculationId={calculationResult?.calculation_id}
              currentResult={calculationResult?.results}
            />
          </div>
        </div>
      )}
    </div>
  );
}
